import { Button } from '@components/Button'
import { View, Text } from 'tamagui'
import { useFormContext } from 'react-hook-form'
import { useMemo } from 'react'
import { useActivityStore } from '@stores/useActivityStore'
import { useExamAnswersCommit } from '@services/api/graphql/examAnswers'
import { useToastController } from '@tamagui/toast'
import { IActivityData } from '@dtos/Activity'
import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'

type FormData = {
  [questionId: string]: string | { [assertionId: string]: string }
}

interface IQuestionFooterProps {
  progressCardId?: string
  enrollmentId: string
  getActivity: LazyQueryExecFunction<IActivityData, OperationVariables>
  mode: 'edit' | 'readonly' | 'retry'
  updateEnrollmentProgress: () => void
}

export function ButtonRetry({
  progressCardId,
  enrollmentId,
  getActivity,
  mode,
  updateEnrollmentProgress,
}: IQuestionFooterProps) {
  const { handleSubmit, watch, formState, reset } = useFormContext<FormData>()
  const { activity, setActivity, startRetry, finishRetry } = useActivityStore()
  const { handleExamAnswersCommit, loading } = useExamAnswersCommit()
  const toast = useToastController()

  // Observa todos os valores do formulário para verificar se há respostas
  const formValues = watch()

  // Verifica se todas as questões foram respondidas
  const hasAnswers = useMemo(() => {
    if (!activity?.examQuestions) return false

    // Verifica cada questão individualmente
    return activity.examQuestions.every((question) => {
      const questionId = question.id
      const value = formValues[questionId]

      // Para questões do tipo string (TrueFalse e MultipleChoice)
      if (
        question.type === 'TrueOrFalseExamQuestion' ||
        question.type === 'MultipleChoiceExamQuestion'
      ) {
        return typeof value === 'string' && value !== undefined && value !== ''
      }

      // Para questões associativas
      if (question.type === 'AssociativeExamQuestion') {
        const secondAssertions = question.secondAssertions || []

        if (typeof value === 'object' && value !== null) {
          const selections = Object.values(value)
          const expectedSelectionsCount = secondAssertions.length

          // Verifica se todas as secondAssertions foram associadas
          return (
            selections.length === expectedSelectionsCount &&
            selections.every((v) => v !== undefined && v !== '')
          )
        } else {
          return false
        }
      }

      return false
    })
  }, [formValues, activity?.examQuestions])

  const disableSubmit = useMemo(() => {
    if (loading || formState.isSubmitting) return true
    if (!hasAnswers) return true
    if (!activity?.retryPermitted) return true

    return false
  }, [hasAnswers, formState.isSubmitting, loading, activity?.retryPermitted])

  const textButton = useMemo(() => {
    if (formState.isSubmitting || loading) return 'Enviando...'
    if (mode === 'retry') return 'Refazer teste'
    return 'Enviar respostas'
  }, [mode, formState.isSubmitting, loading])

  const handleFormSubmit = async (data: FormData) => {
    try {
      if (!activity?.id) {
        throw new Error('Dados da atividade incompletos')
      }

      if (mode === 'retry') {
        // Limpa o formulário e inicia o processo de retry
        reset()
        startRetry()

        toast.show('Formulário limpo! Você pode responder novamente.', {
          type: 'success',
        })
        return
      }

      const examQuestionAnswersAttributes = activity.examQuestions?.map(
        (question) => {
          const answer = data[question.id]

          if (question.type === 'AssociativeExamQuestion') {
            const associativeOptions = Object.entries(
              answer as { [key: string]: string },
            ).map(([assertionId, value]) => ({
              firstAssertionId: Number(value),
              secondAssertionId: Number(assertionId),
            }))

            return {
              examQuestionId: Number(question.id),
              associativeOptionsAttributes: associativeOptions,
            }
          }

          if (question.type === 'TrueOrFalseExamQuestion') {
            return {
              examQuestionId: Number(question.id),
              correct: answer === 'true',
            }
          }

          return {
            examQuestionId: Number(question.id),
            multipleChoiceOptionId: Number(answer),
          }
        },
      )

      const response = await handleExamAnswersCommit({
        examAnswersInput: examQuestionAnswersAttributes || [],
        fetchLessonProgressInput: {
          enrollmentId: Number(enrollmentId),
          lessonId: activity.examLessonId,
          progressCardId: Number(progressCardId),
        },
      })

      if (response.status === 'Ok') {
        const { data } = await getActivity()

        if (data?.activity) {
          setActivity(data.activity)
          const isApproved = data.activity.examAnswer?.approved
          if (isApproved) updateEnrollmentProgress()
        }
        finishRetry()
        toast.show('Respostas enviadas com sucesso!', {
          type: 'success',
        })
      } else {
        throw new Error('Erro ao enviar respostas')
      }
    } catch (error) {
      finishRetry()
      toast.show('Erro ao enviar respostas. Tente novamente.', {
        type: 'error',
      })
    }
  }
  return (
    <Button
      disabled={disableSubmit}
      onPress={handleSubmit(handleFormSubmit)}
      loading={formState.isSubmitting || loading}
      variant={mode === 'retry' ? 'secondary' : 'primary'}
    >
      <Text>{textButton}</Text>
    </Button>
  )
}

export function QuestionFooter({
  progressCardId,
  enrollmentId,
  getActivity,
  mode,
  updateEnrollmentProgress,
}: IQuestionFooterProps) {
  return (
    <View flexDirection="row" justifyContent="flex-end" alignItems="center">
      <View>
        <ButtonRetry
          progressCardId={progressCardId}
          enrollmentId={enrollmentId}
          getActivity={getActivity}
          mode={mode}
          updateEnrollmentProgress={updateEnrollmentProgress}
        />
      </View>
    </View>
  )
}
